/**
 * @namespace ThemeEvents
 * @description Theme-wide custom events. Components dispatch these on themselves so any
 * other component can react without holding a direct reference.
 *
 * @example
 * element.dispatchEvent(new CarouselSelectEvent({ index: 2, total: 5 }));
 * document.addEventListener(ThemeEvents.carouselSelect, (event) => console.log(event.detail.index));
 */
export class ThemeEvents {
  /** @static @constant {string} A carousel moved to a new slide. */
  static carouselSelect = 'carousel:select';
  /** @static @constant {string} A tab strip activated a different panel. */
  static tabSelect = 'tab:select';
  /** @static @constant {string} An accordion row opened or closed. */
  static accordionToggle = 'accordion:toggle';
  /** @static @constant {string} The cart contents changed. */
  static cartUpdate = 'cart:update';
  /** @static @constant {string} A media element (video, 3d model) started playing. */
  static mediaStartedPlaying = 'media:started-playing';
}

/**
 * @typedef {object} CarouselSelectDetail
 * @property {number} index The index of the newly visible slide.
 * @property {number} total The number of slides in the carousel.
 */

/**
 * Dispatched when a carousel scrolls or pages to a new slide.
 * @extends {Event}
 */
export class CarouselSelectEvent extends Event {
  /** @type {CarouselSelectDetail} */
  detail;

  /** @param {CarouselSelectDetail} detail */
  constructor(detail) {
    super(ThemeEvents.carouselSelect, { bubbles: true });
    this.detail = detail;
  }
}

/**
 * @typedef {object} TabSelectDetail
 * @property {string} panelId The id of the panel that became active.
 * @property {number} index The index of the activated tab.
 */

/**
 * Dispatched when a tab strip activates a different panel.
 * @extends {Event}
 */
export class TabSelectEvent extends Event {
  /** @type {TabSelectDetail} */
  detail;

  /** @param {TabSelectDetail} detail */
  constructor(detail) {
    super(ThemeEvents.tabSelect, { bubbles: true });
    this.detail = detail;
  }
}

/**
 * @typedef {object} AccordionToggleDetail
 * @property {boolean} open Whether the row is now open.
 * @property {HTMLDetailsElement} row The row that changed.
 */

/**
 * Dispatched when an accordion row opens or closes.
 * @extends {Event}
 */
export class AccordionToggleEvent extends Event {
  /** @type {AccordionToggleDetail} */
  detail;

  /** @param {AccordionToggleDetail} detail */
  constructor(detail) {
    super(ThemeEvents.accordionToggle, { bubbles: true });
    this.detail = detail;
  }
}

/**
 * @typedef {object} CartUpdateDetail
 * @property {number} itemCount The number of items in the cart after the update.
 * @property {string} [source] Identifier for whatever triggered the update.
 * @property {CartLine[]} [items] The lines just added, when the update was an
 *   addition. Absent on a change or a removal, which is how a listener tells an
 *   "added to cart" moment from any other cart movement.
 */

/**
 * One line as `/cart/add.js` reports it back.
 *
 * @typedef {object} CartLine
 * @property {string} [title] Product and variant together.
 * @property {string} [product_title]
 * @property {string | null} [variant_title]
 * @property {string | null} [image]
 * @property {number} [quantity]
 */

/**
 * Dispatched after the cart has been mutated.
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
  /** @type {CartUpdateDetail} */
  detail;

  /** @param {CartUpdateDetail} detail */
  constructor(detail) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = detail;
  }
}

/**
 * Dispatched when a media element starts playing, so siblings can pause themselves.
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /** @type {{ resource: HTMLElement }} */
  detail;

  /** @param {HTMLElement} resource The element containing the media that started playing. */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = { resource };
  }
}
